import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { endpoints } from '@/api/endpoints'

/** Django: `CSRF_COOKIE_NAME` / `CSRF_HEADER_NAME` по подразбиране. */
const CSRF_COOKIE = 'csrftoken'
const CSRF_HEADER = 'X-CSRFToken'
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete'])

/**
 * Заявките към самата автентикация се справят сами с отказите: „403" при
 * `auth/me/` е нормалното състояние преди вход, а сбъркана парола при
 * `auth/login/` не бива да пренасочва към екрана, на който човекът вече стои.
 */
const SELF_HANDLED = [endpoints.auth.me, endpoints.auth.login, endpoints.auth.logout]

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export function readCookie(name: string): string | null {
  const prefix = `${name}=`
  for (const chunk of document.cookie ? document.cookie.split('; ') : []) {
    if (chunk.startsWith(prefix)) {
      return decodeURIComponent(chunk.slice(prefix.length))
    }
  }
  return null
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // Сесийна бисквитка от Django: нищо не се пази в localStorage.
  withCredentials: true,
  xsrfCookieName: CSRF_COOKIE,
  xsrfHeaderName: CSRF_HEADER,
  headers: { Accept: 'application/json' },
})

// Заглавката се слага и ръчно: axios я добавя сам само при същия произход, а
// така поведението е едно и също и зад обратен пълномощник, и без него.
api.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase()
  if (UNSAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE)
    if (token) {
      config.headers.set(CSRF_HEADER, token)
    }
  }
  return config
})

type AuthFailureHandler = () => void

let onAuthFailure: AuthFailureHandler | null = null

/** Свързва се веднъж в `main.ts`, за да няма кръгов импорт с рутера. */
export function setAuthFailureHandler(handler: AuthFailureHandler | null): void {
  onAuthFailure = handler
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status
    const url = error.config?.url ?? ''
    if (SELF_HANDLED.some((path) => url.includes(path))) {
      return Promise.reject(error)
    }
    if (status === 401) {
      onAuthFailure?.()
    } else if (status === 403) {
      // Със `SessionAuthentication` DRF връща 403 и за „не си влязъл“, и за
      // „нямаш право“. Само `auth/me/` казва кое е: отказ при влязъл човек е
      // съобщение на екрана, не изход.
      // Мрежова грешка или 500 при проверката не са изход.
      void api.get(endpoints.auth.me).catch((probe: AxiosError) => {
        const code = probe.response?.status
        if (code === 401 || code === 403) onAuthFailure?.()
      })
    }
    return Promise.reject(error)
  },
)
